import { Component, OnInit, OnDestroy, OnChanges, Input, SimpleChange, SimpleChanges, HostListener, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar, MatDialog } from '@angular/material';
import { LoginService } from 'src/app/managers/login.service';
import { ClientConfigService } from 'src/app/common/services/client-config.service';
import { Channel } from 'src/app/models/channel';
import { ChannelItem } from 'src/app/models/channel_item';
import { BaseComponent } from 'src/app/common/components/base/base.component';
import { FunctionsService } from 'src/app/common/services/functions.service';
import { RssFeedService } from 'src/app/managers/rssFeed.service';
import { environment } from 'src/environments/environment';
import { CoreResponseMessage, CoreResponseStatus } from 'src/app/contracts/responseMessage/coreResponseMessage';
import { OrderBy } from 'src/app/repositories/base.repository';
import { Store, UnsubscribeCallback } from 'src/app/store/store';
import { SubscriptionDisplayModeActionType } from 'src/app/store/reducers';

@Component({
  selector: 'app-rss-item',
  templateUrl: './rss-item.component.html',
  styleUrls: ['./rss-item.component.css']
})
export class RssItemComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Output() onFavoriteItemAdded: EventEmitter<ChannelItem> = new EventEmitter<ChannelItem>();
  @Output() onFavoriteItemRemoved: EventEmitter<ChannelItem> = new EventEmitter<ChannelItem>();
  @Input() channel: Channel;
  componentHeight: string;
  loading: boolean;
  defaultChannelImageUrl: string;
  defaultChannelItemImageUrl: string;
  itemSingleClicked: boolean;  
  currentItem: ChannelItem;
  titleToDisplay: string;
  private _onInitCalled: boolean;
  private _channelStoreUnsubscription: UnsubscribeCallback;  

  constructor(route: ActivatedRoute, loginManager: LoginService, clientConfig: ClientConfigService, dialog: MatDialog, private _rssFeedService: RssFeedService,
              private _snackBar: MatSnackBar, private _functionsService: FunctionsService, private _store: Store) { 
    super(route, loginManager, clientConfig, dialog);
    this.titleToDisplay = '';
    this._onInitCalled = false;

    this._channelStoreUnsubscription = this._store.subscribeChannelState(() => {
      if (this._store.getSubscriptionDisplayModeState() === SubscriptionDisplayModeActionType.List) {
        this.channel = this._store.getChannelState();
        this.loadChannelItemsAsync();
      }      
    });
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    this.adjustComponentSize();
  }

  ngOnChanges(changes: SimpleChanges) {    
    for (let propName in changes) {
      if (propName === 'channel') {   
        let change = changes[propName];

        if (this.isChannelChanged(change)) {
          if (this._onInitCalled)
            this.loadChannelItemsAsync();
        }
      }                
    }
  }

  //ngOnInit is called only once after ngOnChanges
  ngOnInit() {
    super.ngOnInit();
    this.adjustComponentSize();
    this.loading = false;
    this.dataSource = []; 
    this.defaultChannelImageUrl = environment.defaultChannelImageUrl;
    this.defaultChannelItemImageUrl = environment.defaultChannelItemImageUrl;
    this.itemSingleClicked = false;    
    this.currentItem = null;
    this.channel = this._store.getChannelState();
    this.loadChannelItemsAsync();
    this._onInitCalled = true;

    //force triggering javascript event 'resize' to prevent the layout issue in the mat-drawer section.
    //the issue is that for some browsers, when a vertical scroll bar in side-channel-menu appears, it occupies small space in the mat-drawer area, resulting in
    //covering a portion of content in the mat-drawer 
    window.dispatchEvent(new Event('resize'));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._channelStoreUnsubscription();
  }

  itemClick(itemId: number) {    
    this.itemSingleClicked = true;
    setTimeout(() => {
      if (this.itemSingleClicked) {
        let channelItems: ChannelItem[] = this.dataSource;
        this.currentItem = channelItems.find(item => item.Id === itemId);
      }
    }, 250);
  }

  itemDoubleClick(itemId: number) {
    this.itemSingleClicked = false;
    let channelItems: ChannelItem[] = this.dataSource;
    let channelItem = channelItems.find(i => i.Id === itemId);
    window.open(channelItem.ItemUrl);
  }

  refreshChannelItems() {
    this.loadChannelItemsAsync();
  }

  openInNewTab() {
    if (this.currentItem !== null) {
      window.open(this.currentItem.ItemUrl);
    }    
  }

  copyToClipboard() {
    if (this.currentItem !== null) {           
      this._functionsService.copyToClipboard(this.currentItem.ItemUrl);
      this._snackBar.open('The item URL has been copied to Clipboard', 'OK', { duration: this._successMsgDuration });
    }    
  }

  async addToFavorite() {
    if (this.currentItem !== null) {
      let items: ChannelItem[] = [];
      this.currentItem.Tags = this.channel.Title;
      items.push(this.currentItem);
      let coreRespMsg = await this._rssFeedService.addChannelItemsToFavoriteAsync(this.getLoggedinUser(), items);

      if (coreRespMsg.Status === CoreResponseStatus.OK) {
        this.onFavoriteItemAdded.emit(this.currentItem);
        this._snackBar.open('The subscription item bookmarked.', 'OK', { duration: this._successMsgDuration });
      }      
      else {
        this._snackBar.open('Failed to add the subscription item: ' + coreRespMsg.Message, 'OK', { duration: this._failMsgDuration });
      }
    }
  }

  async removeFromFavorite() {
    if (this.currentItem !== null) {
      let coreRespMsg = await this._rssFeedService.removeChannelItemFromFavoriteAsync(this.getLoggedinUser(), this.currentItem);

      if (coreRespMsg.Status === CoreResponseStatus.OK) {
        let channelItems = this.dataSource.filter(x => x.Id !== this.currentItem.Id);
        this.dataSource = channelItems;
        this.onFavoriteItemRemoved.emit(this.currentItem);
        this._snackBar.open('The item has been removed.', 'OK', { duration: this._successMsgDuration });
      }
      else {
        this._snackBar.open('Failed to remove the channel item: ' + coreRespMsg.Message, 'OK', { duration: this._failMsgDuration });
      }
    }
  }

  isItemAvail(): boolean {
    return this.currentItem !== null;
  }

  isBookmarkItem(): boolean {
    if (this.currentItem !== null && this.titleToDisplay === "Bookmarks")
      return true;
    else
      return false;
  }
  
  private isChannelChanged(change: SimpleChange): boolean {
    let update: boolean = false;
    let prevChannel: Channel = change.previousValue;
    let currChannel: Channel = change.currentValue;
    
    if (currChannel !== undefined) {
      if (prevChannel === undefined)
        update = true;
      else if (prevChannel.Title !== currChannel.Title)
        update = true;
    }

    return update;
  }

  private async loadChannelItemsAsync() {
    this.loading = true;
    this.dataSource = [];
    this.currentItem = null;
    let channelItems: ChannelItem[];      
    let coreRespMsg: CoreResponseMessage; 
    
    if (this.channel.Title === 'Favorite_Builtin') {
      this.titleToDisplay = "Bookmarks"
      coreRespMsg = await this._rssFeedService.getFavoriteItemsAsync(this.getLoggedinUser(), 0, 0, 'PublishDate', OrderBy.DESC.toString());
    }
    else {
      this.titleToDisplay = this.channel.Title;
      coreRespMsg = await this._rssFeedService.loadRssItemsAsync(this.getLoggedinUser(), this.channel);
    }

    if (coreRespMsg.Status === CoreResponseStatus.OK)
      channelItems = coreRespMsg.Result;                
    else {
      channelItems = [];

      if (coreRespMsg.Status !== CoreResponseStatus.NoData) {
        this._snackBar.open('Failed to load feeds: ' + coreRespMsg.Message, 'OK', { duration: this._failMsgDuration });
        this.handleCoreResponseMessageError(coreRespMsg);
      } 
    } 
          
    this.dataSource = channelItems;      
    this.loading = false;
  }

  private setChannelItems(coreRespMsg: CoreResponseMessage): ChannelItem[] {
    let channelItems: ChannelItem[] = coreRespMsg.Result;

    channelItems.forEach(x => {
      let rex = /<img[^>]+src="([^">]+)/g;
      let imageUrls = [];
      let m;

      while (m = rex.exec(x.Description)) {
        imageUrls.push(m[1]);
      }

      x.ImageUrl = imageUrls[0];
    });

    return channelItems;
  }

  private adjustComponentSize() {
    let screenHeight: number = window.innerHeight;
    let screenWidth: number = window.innerWidth;

    this.componentHeight = screenHeight - this._functionsService.getSubtractionForAppHeight(this.isUserAdmin()) + 'px';
  }
}
